import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { useApolloClient, useQuery } from '@apollo/client';
import gql from 'graphql-tag';
import Modal from '~ComponentsImproved/Modal';
import { insideWrapperStyles } from '~ComponentsImproved/Modal';
import { Box, BlueButton, BlackButton } from '~Styles';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useButton, useI18n, useLogger, useSelector } from '~Hooks';
import { useTableUniverse } from '~Components/TableUniverse';
import Grid from '~Scenes/Tables/Scenes/Grid';
import Floorplan from '~Scenes/Tables/Scenes/Floorplan';
import { Content } from '~Scenes/Tables';
import { FloorPlans } from '~Scenes/ParkedOrders/Components/ParkedOrderDetails/MoveToTable/__queries__';

const Wrapper = styled(Box)`
  box-shadow: ${insideWrapperStyles.boxShadow};
  border-radius: ${insideWrapperStyles.borderRadius};
  background-color: ${insideWrapperStyles.backgroundColor};
  min-width: 600px;
  max-width: 90vw;
  width: 100%;
  height: 80vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ViewTypesWrapper = styled.div`
  background-color: var(--colorBlueGray1100);
  width: 176px;
  display: flex;
  flex-flow: column nowrap;
  flex-shrink: 0;
`;

const ViewType = styled.div<{ selected: boolean }>`
  height: 64px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.24px;
  color: var(--colorWhite);
  padding: 8px 12px 8px 8px;
  border-left: 4px solid transparent;
  border-left-color: ${(props) => (props.selected ? 'var(--colorUIYellow)' : 'transparent')};
  border-bottom: 1px solid var(--colorBlueGray1200);
  background: ${(props) =>
    props.selected
      ? 'linear-gradient(90deg, rgb(14, 18, 22) 0%, rgb(28, 35, 44) 100%)'
      : 'var(--colorBlueGray1100)'};
`;

const FloorplanArea = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const SelectedTablesBar = styled(Box)`
  background-color: var(--colorBlueGray1100);
  border-top: 1px solid var(--colorBlueGray900);
  padding: 12px 24px;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SelectedTableChip = styled.div`
  background-color: var(--colorBlueGray900);
  border: 1px solid var(--colorUIYellow);
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 14px;
  font-weight: 600;
  color: var(--colorWhite);
`;

const ButtonWrapper = styled(Box)`
  background-color: var(--colorBlueGray1200);
  padding: 16px 24px;
  flex-shrink: 0;
`;

const HeaderWrapper = styled(Box)`
  padding: 24px 24px 12px;
  flex-shrink: 0;
`;

interface Props {
  isShowing: boolean;
  toggle: () => void;
  selectedTableIds: string[];
  onConfirm: (tableIds: string[], tableCodes: string[]) => void | Promise<void>;
  title?: string;
  confirmLabel?: string;
  confirmingLabel?: string;
  isProcessing?: boolean;
}

/**
 * Reusable modal for selecting tables from floorplans.
 * Supports multi-select by toggling individual tables.
 */
const TableSelectModal: React.FC<Props> = ({
  isShowing,
  toggle,
  selectedTableIds: externalSelectedTableIds,
  onConfirm,
  title,
  confirmLabel,
  confirmingLabel,
  isProcessing = false,
}) => {
  const { i18n } = useI18n();
  const logger = useLogger('table-select-modal');
  const client = useApolloClient();
  const venueId = useSelector((state) => state.venueId) as string;
  const tableUniverse = useTableUniverse();
  const allCheques = tableUniverse.cheques;

  const [selectedTableIds, setSelectedTableIds] = useState<string[]>([]);
  const [selectedNav, setSelectedNav] = useState<string>('grid');

  // Read floor plans from Apollo cache
  const tablesData = useMemo(() => {
    try {
      return client.readFragment<FloorPlans>({
        id: venueId,
        fragment: gql`
          fragment TableSelectFloorPlans on Venue {
            id

            floorPlans {
              id
              name

              tables {
                id
                code
                rotation
                posX
                posY
                deleted

                floorPlan {
                  id
                }
              }

              markers {
                id
                posX
                posY
                size
                icon
              }

              structures {
                id
                posX
                posY
                width
                height
                rotation
              }
            }
          }
        `,
      });
    } catch (e) {
      logger.error('Failed to read floor plans from cache', { reason: String(e) });
      return null;
    }
  }, [venueId]);

  const floorPlans = tablesData?.floorPlans || [];
  const allTables = floorPlans.flatMap((fp) => fp.tables).filter((t) => !t.deleted) || [];

  const selectedFloorPlanData = floorPlans.find((fp) => fp.id === selectedNav);
  const markers = selectedFloorPlanData?.markers || [];
  const structures = selectedFloorPlanData?.structures || [];

  // Sync with external selection when modal opens
  useEffect(() => {
    if (isShowing) {
      setSelectedTableIds(externalSelectedTableIds);
    }
  }, [isShowing]);

  // Toggle table selection (multi-select)
  const handleTableSelect = (id: string) => {
    setSelectedTableIds((prev) => {
      if (prev.includes(id)) {
        return prev.filter((tableId) => tableId !== id);
      }
      return [...prev, id];
    });
  };

  // Get table code by ID
  const getTableCode = (tableId: string): string => {
    return allTables.find((t) => t.id === tableId)?.code || i18n('Bookings.Unknown');
  };

  const cancelButton = useButton({
    onClick: () => {
      setSelectedTableIds([]);
      toggle();
    },
    disabled: isProcessing,
  });

  const confirmButton = useButton({
    onClick: () => {
      logger.info('Tables selected', { tableIds: selectedTableIds });
      const codes = selectedTableIds.map((id) => getTableCode(id));
      onConfirm(selectedTableIds, codes);
    },
    disabled: selectedTableIds.length === 0 || isProcessing,
  });

  return (
    <Modal isShowing={isShowing} hide={toggle}>
      <Wrapper>
        <HeaderWrapper>
          <Paragraph level={2} color="var(--colorWhite)">
            {title || i18n('Bookings.SelectTables')}
          </Paragraph>
        </HeaderWrapper>

        <FloorplanArea>
          <ViewTypesWrapper>
            <ViewType
              selected={selectedNav === 'grid'}
              onTouchStart={() => setSelectedNav('grid')}>
              {i18n('TM.Grid')}
            </ViewType>
            {floorPlans.map((floorPlan) => (
              <ViewType
                key={floorPlan.id}
                selected={selectedNav === floorPlan.id}
                onTouchStart={() => setSelectedNav(floorPlan.id)}>
                {floorPlan.name}
              </ViewType>
            ))}
          </ViewTypesWrapper>

          <Content>
            {selectedNav === 'grid' ? (
              <Grid
                tables={allTables}
                allCheques={allCheques}
                selectedTable={undefined}
                selectedTableIds={selectedTableIds}
                showOnlyOpen={false}
                setSelectedTable={handleTableSelect}
              />
            ) : (
              selectedNav.length > 0 && (
                <Floorplan
                  allTables={allTables}
                  markers={markers}
                  structures={structures}
                  selectedFloorPlan={selectedNav}
                  allCheques={allCheques}
                  selectedTable={undefined}
                  selectedTableIds={selectedTableIds}
                  setSelectedTable={handleTableSelect}
                />
              )
            )}
          </Content>
        </FloorplanArea>

        {/* Show selected tables */}
        {selectedTableIds.length > 0 && (
          <SelectedTablesBar>
            <Paragraph level={4} color="var(--colorBlueGray200)" bottom={0}>
              {i18n('Bookings.SelectedTables')}:
            </Paragraph>
            {selectedTableIds.map((tableId) => (
              <SelectedTableChip key={tableId}>{getTableCode(tableId)}</SelectedTableChip>
            ))}
          </SelectedTablesBar>
        )}

        <ButtonWrapper display="flex" gap="small">
          <BlackButton fluid {...cancelButton}>
            {i18n('General.Cancel')}
          </BlackButton>
          <BlueButton fluid {...confirmButton}>
            {isProcessing
              ? confirmingLabel || i18n('General.Processing')
              : confirmLabel || i18n('Bookings.ConfirmSelection')}
          </BlueButton>
        </ButtonWrapper>
      </Wrapper>
    </Modal>
  );
};

export default TableSelectModal;
